/**
 * Browser-extension / injected-wallet noise, as ONE list shared by three
 * readers that must agree (design-rules.md §12.6):
 *
 *   - src/lib/errorLogger.ts   drops it before a client_errors row is written
 *   - src/instrument.ts        drops it in Sentry's beforeSend
 *   - scripts/monitor-critical-errors.mjs  discounts rows that were written
 *     before the client filter shipped (or by an old cached bundle)
 *
 * Plain ESM JavaScript, not TypeScript, on purpose: the monitor runs under bare
 * `node` in CI with `npm ci --omit=dev`, so it can import this file directly
 * with no build step, and the TS app imports it through `allowJs`.
 *
 * Triggering case (2026-11-04): client_errors carried
 * "TypeError: undefined is not an object (evaluating
 * 'window.ethereum.selectedAddress = undefined')" from three different public
 * pages. That is a crypto-wallet extension's injected provider script running
 * in Safari. Nothing in src/ or api/ references window.ethereum or MetaMask
 * (grep-verified), so the message cannot originate in our bundle.
 *
 * Keep this list NARROW. Each entry must name a symbol our code never touches.
 * A generic word ("extension", "wallet") would also hide real errors.
 */

/** Lower-case substrings. Matched against the lower-cased message. */
export const EXTENSION_NOISE_MESSAGE_PATTERNS = [
  // Injected EIP-1193 wallet providers (MetaMask, Coinbase, Trust, Phantom's
  // EVM shim...) touching window.ethereum.
  "window.ethereum",
  "cannot redefine property: ethereum",
  "cannot set property ethereum",
  "metamask",
];

/** chrome-, moz-, safari-web-, safari-, ms-browser- extension URL schemes. */
export const EXTENSION_SCHEME_RE =
  /(^|[^a-z0-9.+-])(chrome|moz|safari-web|safari|ms-browser)-extension:\/\//i;

/**
 * URL of the FIRST stack frame (the frame that threw). Frame-shaped lines only,
 * so a URL in the message header is never mistaken for an origin. Handles V8
 * `    at fn (url:1:2)` and JSC/SpiderMonkey `fn@url:1:2`.
 */
export function firstStackFrameUrl(stack) {
  if (!stack) return null;
  for (const raw of String(stack).split("\n")) {
    const line = raw.trim();
    if (!/^at\s/.test(line) && !line.includes("@")) continue;
    const match = /\b[a-z][a-z0-9.+-]*:\/\/[^\s)'"]+/i.exec(line);
    if (match) return match[0];
  }
  return null;
}

/** True when a known extension symbol appears in the message text. */
export function isExtensionNoiseMessage(message) {
  const lower = String(message ?? "").toLowerCase();
  return EXTENSION_NOISE_MESSAGE_PATTERNS.some((p) => lower.includes(p));
}

/**
 * True when an error row / event is extension noise:
 *   1. its message names a known extension symbol, or
 *   2. the FIRST line of its message names an extension URL (e.g. a resource
 *      load failure for chrome-extension://.../inject.js), or
 *   3. the frame that THREW (first frame of `stack`, else of `message`, since
 *      console_error rows often inline the stack) is an extension URL.
 *
 * Deliberately NOT "an extension URL appears anywhere". An extension that wraps
 * window.fetch shows up mid-stack on real bugs in our code; those stay logged.
 *
 * @param {{ message?: string | null, stack?: string | null }} row
 */
export function isExtensionNoise(row) {
  const message = String(row?.message ?? "");
  if (isExtensionNoiseMessage(message)) return true;
  const header = message.split("\n", 1)[0];
  if (EXTENSION_SCHEME_RE.test(header)) return true;
  const frame = firstStackFrameUrl(row?.stack || message);
  return frame !== null && EXTENSION_SCHEME_RE.test(frame);
}
